import React, { useEffect, useMemo, useRef, useState } from 'react';
import SelectMe from 'react-select-me';

import { formatCurrency } from '../../../../common-utils/formatters';
import type { User } from '../../../@types/User';
import { showToast } from '../../common/Notification';
import Popup from '../../common/Popup';
import Table from '../../common/Table';
import { computePerformanceAttribution } from './performanceAttribution';
import {
  buildEvenDistribution,
  filterTransactionnaires,
  renderUserLabel,
  sumPercentages,
} from './smartAttributionUtils';
import type {
  SmartAttributionCluster,
  SmartAttributionCoproInput,
  SmartAttributionDialogProps,
  SmartAttributionResult,
  SmartAttributionTarget,
} from './types';
import { DEBUG_DEFAULTS } from './types';

const MIN_SALES_THRESHOLD = 2;
const percentageFormatter = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 });

const headers = [
  'Transactionnaire',
  'Objectif %',
  'Objectif CA',
  'Attribution CA',
  'Couverture vs cible',
  'Nombre de copros',
];

type Step = 'config' | 'result';

interface PerformanceAttributionDialogProps extends SmartAttributionDialogProps {}

interface RankedTarget extends SmartAttributionTarget {
  order: number;
}

const buildUserLookup = (users: User[]) => users.reduce<Record<number, User>>((acc, user) => {
  acc[user.id] = user;
  return acc;
}, {});

const renderClusterRow = (
  cluster: SmartAttributionCluster,
  userLookup: Record<number, User>,
  totalSales: number,
) => {
  const user = userLookup[cluster.userId];
  const pct = cluster.coveragePercentage;
  const coverageDiff = pct - cluster.targetPercentage;
  const diffText = coverageDiff === 0 ? '0%' : `${coverageDiff > 0 ? '+' : ''}${coverageDiff.toFixed(1)}%`;

  return {
    Transactionnaire: user ? renderUserLabel(user) : 'Utilisateur inconnu',
    'Objectif %': `${cluster.targetPercentage}%`,
    'Objectif CA': formatCurrency(cluster.targetSalesAmount || 0),
    'Attribution CA': formatCurrency(cluster.assignedSalesAmount || 0),
    'Couverture vs cible': `${percentageFormatter.format(pct)}% (${diffText})`,
    'Nombre de copros': cluster.assignments.length,
    id: cluster.userId,
    rawAssignments: cluster.assignments,
    totalSales,
  };
};

interface RankingActionProps {
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
}

const RankingActions = ({ onMoveUp, onMoveDown, isFirst, isLast }: RankingActionProps) => (
  <div className="flex gap-2">
    <button
      type="button"
      onClick={onMoveUp}
      disabled={isFirst}
      className="rounded border border-slate-300 px-2 py-1 text-xs disabled:opacity-40"
    >
      ↑ Prioriser
    </button>
    <button
      type="button"
      onClick={onMoveDown}
      disabled={isLast}
      className="rounded border border-slate-300 px-2 py-1 text-xs disabled:opacity-40"
    >
      ↓ Déprioriser
    </button>
  </div>
);

const PerformanceAttributionDialog = ({
  isOpen,
  onClose,
  companyUsers,
  salesEntries,
  totalCoprosCount,
  onApply,
}: PerformanceAttributionDialogProps) => {
  const [step, setStep] = useState<Step>('config');
  const [targets, setTargets] = useState<RankedTarget[]>([]);
  const [result, setResult] = useState<SmartAttributionResult | null>(null);
  const [availableTransactionnaires, setAvailableTransactionnaires] = useState<User[]>([]);
  const [copros, setCopros] = useState<SmartAttributionCoproInput[]>([]);
  const [excludeLowSales, setExcludeLowSales] = useState(false);
  const [isComputing, setIsComputing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const userLookup = useMemo(() => buildUserLookup(companyUsers), [companyUsers]);

  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      return;
    }

    if (wasOpenRef.current) {
      return;
    }
    wasOpenRef.current = true;

    const transactionnaires = filterTransactionnaires(companyUsers);
    setAvailableTransactionnaires(transactionnaires);

    setTargets((current) => {
      if (current.length > 0) {
        return current.map((target, index) => ({ ...target, order: index }));
      }
      const suggested = transactionnaires.slice(0, Math.min(transactionnaires.length, 3));
      return buildEvenDistribution(suggested).map((target, index) => ({ ...target, order: index }));
    });

    setStep('config');
    setResult(null);
    setExcludeLowSales(false);
    setCopros(salesEntries.map((entry) => ({
      id: entry.id,
      name: entry.name,
      salesAmount: Number(entry.salesAmount ?? 0),
      salesCount: entry.salesCount ?? null,
      lat: null,
      long: null,
    })));
  }, [isOpen, companyUsers, salesEntries]);

  const filteredCopros = useMemo(() => {
    if (!excludeLowSales) {
      return copros;
    }
    return copros.filter((copro) => (copro.salesCount ?? 0) >= MIN_SALES_THRESHOLD);
  }, [copros, excludeLowSales]);

  const filteredCoprosCount = useMemo(() => {
    if (!excludeLowSales) {
      return totalCoprosCount;
    }
    return salesEntries.filter((entry) => (entry.salesCount ?? 0) >= MIN_SALES_THRESHOLD).length;
  }, [excludeLowSales, salesEntries, totalCoprosCount]);

  const handleUserSelection = (selectedUserIds: number[]) => {
    setTargets(() => {
      const selectedUsers = availableTransactionnaires.filter((candidate) => selectedUserIds.includes(candidate.id));
      const nextTargets = buildEvenDistribution(selectedUsers).map((target, index) => ({ ...target, order: index }));
      return nextTargets;
    });
    setResult(null);
  };

  const handlePercentageChange = (userId: number, percentage: number) => {
    const nextValue = Number.isFinite(percentage) ? Math.max(0, percentage) : 0;
    setTargets((current) => current.map((target) => (target.user.id === userId ? { ...target, percentage: nextValue } : target)));
    setResult(null);
  };

  const moveTarget = (index: number, direction: -1 | 1) => {
    setTargets((current) => {
      const next = [...current];
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= next.length) {
        return current;
      }
      const [item] = next.splice(index, 1);
      next.splice(newIndex, 0, item);
      return next.map((target, idx) => ({ ...target, order: idx }));
    });
    setResult(null);
  };

  const totalPercentage = useMemo(() => sumPercentages(targets), [targets]);
  const canCompute = targets.length > 0 && Math.abs(totalPercentage - 100) < 0.5 && targets.every((target) => target.percentage > 0);
  const isBusy = isComputing || isSubmitting;

  const runComputation = async() => {
    if (!canCompute) {
      showToast('error', 'Les pourcentages doivent totaliser 100% et être strictement positifs.');
      return;
    }

    if (filteredCopros.length === 0) {
      showToast('error', 'Aucune copropriété disponible pour l’attribution.');
      return;
    }

    setIsComputing(true);
    try {
      const inputTargets = targets.map((target) => ({
        userId: target.user.id,
        percentage: target.percentage,
        anchor: undefined,
      }));
      const resultValue = computePerformanceAttribution({
        copros: filteredCopros,
        targets: inputTargets,
        allowAnchorsFallback: false,
        debugParams: DEBUG_DEFAULTS,
      });
      setResult(resultValue);
      setStep('result');
    } catch (error) {
      console.error('[PerformanceAttributionDialog] compute error', error);
      showToast('error', 'Impossible de calculer l’attribution. Veuillez réessayer.');
    } finally {
      setIsComputing(false);
    }
  };

  const handleApply = async() => {
    if (!result) {
      return;
    }
    setIsSubmitting(true);
    try {
      await onApply(result.clusters);
      onClose();
    } catch (error) {
      console.error('[PerformanceAttributionDialog] apply error', error);
      showToast('error', 'Une erreur est survenue lors de l’enregistrement des attributions.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderConfigStep = () => (
    <div className="space-y-8">
      <div className="space-y-2">
        <p className="text-sm text-gray-600">
          Classez les transactionnaires en fonction de leur capacité à gérer les grands comptes, définissez les objectifs de CA et excluez les copropriétés peu actives si nécessaire.
        </p>
      </div>

      <div>
        <label className="mb-2 block font-semibold">Transactionnaires concernés</label>
        <SelectMe
          multiple
          closeOnSelect={false}
          value={targets.map((target) => ({ value: target.user.id, label: renderUserLabel(target.user) }))}
          onChange={(options) => {
            const arrayOptions = Array.isArray(options) ? options : [];
            const nextSelectedIds = arrayOptions.map((opt) => Number(opt.value)).filter((value) => !Number.isNaN(value));
            handleUserSelection(nextSelectedIds);
          }}
          options={availableTransactionnaires.map((user) => ({
            value: user.id,
            label: renderUserLabel(user),
          }))}
          placeholder="Sélectionner les transactionnaires"
        />
      </div>

      <div className="space-y-4">
        {targets.map((target, index) => (
          <div key={target.user.id} className="grid gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4 md:grid-cols-[2fr_1fr_auto] md:items-center">
            <div>
              <p className="font-semibold text-gray-800">
                #{index + 1}
                {' '}
                {renderUserLabel(target.user)}
              </p>
              <p className="text-xs text-gray-500">Les meilleurs collaborateurs seront servis en premier.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600">Objectif de CA (%)</label>
              <input
                type="number"
                min={0}
                max={100}
                value={target.percentage}
                onChange={(event) => handlePercentageChange(target.user.id, Number(event.target.value))}
                className="w-full rounded border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-rgpd-blue-light focus:outline-none"
                step="1"
              />
            </div>
            <RankingActions
              onMoveUp={() => moveTarget(index, -1)}
              onMoveDown={() => moveTarget(index, 1)}
              isFirst={index === 0}
              isLast={index === targets.length - 1}
            />
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between border-t pt-4">
        <div>
          <p className="text-sm text-gray-600">
            Total assigné :
            {' '}
            <span className="font-semibold">{totalPercentage}%</span>
          </p>
          {Math.abs(totalPercentage - 100) >= 0.5 && (
            <p className="text-xs text-red-600">Le total des pourcentages doit être exactement égal à 100%.</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            role="switch"
            aria-label={`Exclure les copros avec moins de ${MIN_SALES_THRESHOLD} ventes`}
            aria-checked={excludeLowSales}
            onClick={() => setExcludeLowSales((previous) => !previous)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${excludeLowSales ? 'bg-rgpd-blue-light' : 'bg-gray-300'}`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${excludeLowSales ? 'translate-x-5' : 'translate-x-1'}`}
            />
          </button>
          <div className="flex flex-col text-right text-sm text-gray-700">
            <span>
              Exclure les copros {'< '}
              {MIN_SALES_THRESHOLD}
              {' '}
              ventes
            </span>
            <span className="text-xs text-gray-500">
              {filteredCoprosCount}
              /
              {totalCoprosCount}
              {' '}
              éligibles pour l’attribution
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={runComputation}
          disabled={!canCompute || isComputing}
          className="rounded bg-rgpd-blue-dark px-4 py-2 text-white disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isComputing ? 'Calcul en cours…' : 'Calculer la proposition'}
        </button>
      </div>
    </div>
  );

  const renderResultStep = () => {
    if (!result) {
      return null;
    }

    const tableData = result.clusters.map((cluster) => renderClusterRow(cluster, userLookup, result.totalSalesAmount));

    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Synthèse de l’attribution</h3>
            <p className="text-sm text-gray-600">
              Les copropriétés sont attribuées du meilleur transactionnaire vers le moins prioritaire, selon leurs objectifs de CA.
            </p>
          </div>
          <div className="overflow-x-auto">
            <Table
              data={tableData}
              headers={headers}
              idKey="id"
              tableClass="min-w-full bg-white border text-sm"
            />
          </div>
          <div className="text-sm text-gray-600">
            Total CA traité :
            {' '}
            <span className="font-semibold">{formatCurrency(result.totalSalesAmount)}</span>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setStep('config')}
            className="rounded border border-gray-300 px-4 py-2 text-sm"
            disabled={isSubmitting}
          >
            Retour
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="rounded bg-rgpd-blue-dark px-4 py-2 text-sm text-white disabled:cursor-not-allowed disabled:opacity-50"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Attribution en cours…' : 'Appliquer l’attribution'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <Popup
      isOpen={isOpen}
      onClose={() => {
        if (isBusy) {
          return;
        }
        onClose();
      }}
      title="Attribution par CA des copros / salariés"
      additionalClass="w-full max-w-4xl"
      disableCloseButton={isSubmitting}
    >
      <div className="flex flex-col gap-4">
        <div className="space-y-6">
          {copros.length === 0 ? (
            <div className="rounded border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">
              Aucune donnée de ventes disponible pour lancer l’attribution.
            </div>
          ) : (
            <>
              {step === 'config' && renderConfigStep()}
              {step === 'result' && renderResultStep()}
            </>
          )}
        </div>
      </div>
    </Popup>
  );
};

export default PerformanceAttributionDialog;

